import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  TREQUEST_LotCompletion,
  TREQUEST_LotDetail,
  TREQUEST_LotListing,
  TREQUEST_RollInspectionSync,
  TREQUEST_RollsCountInfo,
  TREQUEST_StartOrUpdateLotInspection,
  TREQUEST_UpdateRollStatus,
  TREQUEST_ChangeRollStatus,
  TRESPONSE_FabricInspectionConfig,
  TRESPONSE_LotCompletion,
  TRESPONSE_LotDetail,
  TRESPONSE_LotListing,
  TRESPONSE_RollInspectionSync,
  TRESPONSE_RollsCountInfo,
  TRESPONSE_StartOrUpdateLotInspection,
  TLotListingType,
  TRESPONSE_UpdateRollStatus,
  TREQUEST_RollSplitRequest,
  TRESPONSE_RollSplitResponse,
  TREQUEST_CommentForInspectedRollRequest,
  TRESPONSE_CommentForInspectedRollResponse,
  TRESPONSE_FabricInspectionDefectsList,
  TREQUEST_CommentForInspectedLot,
  TRESPONSE_CommentForInspectedLot,
  TREQUEST_getLotDetailbyGrnTag,
  TRESPONSE_getLotDetailbyGrnTag,
} from '@monorepo/types';
import { FabricInspectionEnvService } from './fabric-inspection-env.service';

@Injectable({
  providedIn: 'root',
})
export class FabricInspectionDataService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(public http: HttpClient, public fabricInspectionEnvService: FabricInspectionEnvService) {}

  fetchFabricDefectsListingV2(factoryId: string) {
    const url = this.fabricInspectionEnvService.getEnvVar('DEFECTS_LISTING_V2') + `/${factoryId}`;
    return this.http.post<TRESPONSE_FabricInspectionDefectsList>(url, {}, this.httpOptions);
  }

  fetchRollsCountInfo(rollsCountInfoRequestPayload: TREQUEST_RollsCountInfo) {
    const url = this.fabricInspectionEnvService.getEnvVar('ROLLS_INFO');
    return this.http.post<TRESPONSE_RollsCountInfo>(url, rollsCountInfoRequestPayload, this.httpOptions);
  }

  fetchLotListing(lotListingRequestPayload: TREQUEST_LotListing, lotListingType: TLotListingType) {
    const url = this.fabricInspectionEnvService.getEnvVar('LOT_LISTING') + lotListingType;
    return this.http.post<TRESPONSE_LotListing>(url, lotListingRequestPayload, this.httpOptions);
  }

  fetchLotDetail(lotDetailRequestPayload: TREQUEST_LotDetail) {
    const url = this.fabricInspectionEnvService.getEnvVar('LOT_DETAIL');
    return this.http.post<TRESPONSE_LotDetail>(url, lotDetailRequestPayload);
  }

  fetchFabricInspectionConfig(factoryId: string) {
    const url = this.fabricInspectionEnvService.getEnvVar('FI_CONFIG') + factoryId;
    return this.http.get<TRESPONSE_FabricInspectionConfig>(url, this.httpOptions);
  }

  startOrUpdateLotInspection(payload: TREQUEST_StartOrUpdateLotInspection) {
    const url = this.fabricInspectionEnvService.getEnvVar('START_OR_UPDATE_LOT_INSPECTION');
    return this.http.post<TRESPONSE_StartOrUpdateLotInspection>(url, payload, this.httpOptions);
  }

  /** Re-open inspection for a failed lot (same payload as start/update). */
  reopenLotInspection(payload: TREQUEST_StartOrUpdateLotInspection) {
    const url = this.fabricInspectionEnvService.getEnvVar('REOPEN_LOT_INSPECTION');
    return this.http.post<TRESPONSE_StartOrUpdateLotInspection>(url, payload, this.httpOptions);
  }

  updateRollStatus(payload: TREQUEST_UpdateRollStatus) {
    const url = this.fabricInspectionEnvService.getEnvVar('UPDATE_ROLL_STATUS');
    return this.http.post<TRESPONSE_UpdateRollStatus>(url, payload, this.httpOptions);
  }

  changeRollStatus(payload: TREQUEST_ChangeRollStatus) {
    const url = this.fabricInspectionEnvService.getEnvVar('CHANGE_ROLL_STATUS');
    return this.http.post<TRESPONSE_UpdateRollStatus>(url, payload, this.httpOptions);
  }

  syncRollInspection(payload: TREQUEST_RollInspectionSync) {
    const url = this.fabricInspectionEnvService.getEnvVar('ROLL_INSPECTION_SYNC');
    return this.http.post<TRESPONSE_RollInspectionSync>(url, payload, this.httpOptions);
  }

  completeLotInspection(payload: TREQUEST_LotCompletion) {
    const url = this.fabricInspectionEnvService.getEnvVar('LOT_COMPLETION');
    return this.http.post<TRESPONSE_LotCompletion>(url, payload, this.httpOptions);
  }

  fetchTransactionLotListing(lotListingRequestPayload: TREQUEST_LotListing, lotListingType: TLotListingType) {
    const url = this.fabricInspectionEnvService.getEnvVar('LOT_LISTING') + lotListingType;
    return this.http.post<TRESPONSE_LotListing>(url, lotListingRequestPayload);
  }

  fetchTransactionLotDetail(lotDetailRequestPayload: TREQUEST_LotDetail) {
    const url = this.fabricInspectionEnvService.getEnvVar('LOT_DETAIL');
    return this.http.post<TRESPONSE_LotDetail>(url, lotDetailRequestPayload);
  }

  splitRoll(payload: TREQUEST_RollSplitRequest) {
    const url = this.fabricInspectionEnvService.getEnvVar('SPLIT_ROLL');
    return this.http.post<TRESPONSE_RollSplitResponse>(url, payload, this.httpOptions);
  }

  commentOnRoll(payload: TREQUEST_CommentForInspectedRollRequest) {
    const url = this.fabricInspectionEnvService.getEnvVar('COMMENT_ON_ROLL');
    return this.http.post<TRESPONSE_CommentForInspectedRollResponse>(url, payload, this.httpOptions);
  }

  commentOnLot(payload: TREQUEST_CommentForInspectedLot) {
    const url = this.fabricInspectionEnvService.getEnvVar('COMMENT_ON_LOT');
    return this.http.post<TRESPONSE_CommentForInspectedLot>(url, payload, this.httpOptions);
  }

  getTestReport(fileKey: string) {
    const url = this.fabricInspectionEnvService.getEnvVar('GET_TEST_REPORT');
    return this.http.get(url, { responseType: 'blob', params: { key: fileKey } });
  }

  getLotDetailsByGrnTag(payload: TREQUEST_getLotDetailbyGrnTag) {
    const url = this.fabricInspectionEnvService.getEnvVar('GET_LOT_DETAILS_BY_GRNTAG');
    return this.http.post<TRESPONSE_getLotDetailbyGrnTag>(url, payload);
  }
}
